<template>
  <div class="psychogram-manager">
    <h1>Manajemen Psikogram</h1>
    <p class="subtitle">Atur struktur aspek, sub-aspek, dan tes yang terhubung untuk setiap template.</p>

    <!-- BAGIAN 1: MANAJEMEN TEMPLATE -->
    <div class="card">
      <h2>Template Psikogram</h2>
      <form @submit.prevent="addTemplate" class="inline-form">
        <input v-model="newTemplateName" placeholder="Nama Template Baru" required />
        <button type="submit" :disabled="isCreatingTemplate">
          {{ isCreatingTemplate ? 'Membuat...' : 'Buat Template' }}
        </button>
      </form>
      <hr>
      <div class="template-selector">
        <label for="template-select">Pilih Template untuk Dikelola</label>
        <select id="template-select" v-model="selectedTemplateId">
          <option :value="null">-- Pilih Template --</option>
          <option v-for="template in templates" :key="template.id" :value="template.id">
            {{ template.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Tampilkan editor hanya jika template dipilih -->
    <div v-if="selectedTemplateId" class="manager-grid">
      
      <!-- BAGIAN 2: STRUKTUR TEMPLATE (ASPEK & SUB-ASPEK) -->
      <div class="card">
        <h2>Struktur Template</h2>
        
        <!-- Form untuk menambah Aspek -->
        <form @submit.prevent="addAspect" class="add-form sub-form">
          <input v-model="newAspect.name" placeholder="Nama Aspek Baru" required />
          <input v-model.number="newAspect.order" type="number" placeholder="Urutan" min="1" required />
          <button type="submit" :disabled="isSavingAspect">{{ isSavingAspect ? '...' : 'Tambah Aspek' }}</button>
        </form>
        <hr>
        
        <!-- Form untuk menghubungkan Sub-Aspek -->
        <form @submit.prevent="addSubAspectToAspect" class="add-form sub-form">
          <select v-model="subAspectAssociation.aspect_id" required>
            <option disabled value="">Pilih Aspek Induk</option>
            <option v-for="aspect in sortedAspects" :key="aspect.id" :value="aspect.id">{{ aspect.name }}</option>
          </select>
          <select v-model="subAspectAssociation.sub_aspect_id" required>
            <option disabled value="">Pilih Sub-Aspek dari Master</option>
            <option v-for="sub in masterSubAspects" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
          </select>
          <button type="submit">Hubungkan Sub-Aspek</button>
        </form>
        
        <!-- Tampilan Daftar Aspek dan Sub-Aspek yang Terhubung -->
        <div v-for="aspect in sortedAspects" :key="`group-${aspect.id}`" class="aspect-group">
          <div class="aspect-header">
            <h3>{{ aspect.order }}. {{ aspect.name }}</h3>
            <button @click="deleteAspect(aspect.id)" class="delete-btn small">Hapus Aspek</button>
          </div>
          <ul v-if="aspect.sub_aspects && aspect.sub_aspects.length > 0" class="item-list sub-item-list">
            <li v-for="sub in aspect.sub_aspects" :key="sub.id" class="item sub-aspect-item">
              <div class="sub-aspect-info">
                <span class="sub-aspect-name">{{ sub.name }}</span>
                <div class="sub-aspect-desc-preview">
                  <small v-if="sub.description" class="desc-text"><i style="color:#aaa;">Umum:</i> {{ sub.description }}</small>
                  <small v-if="sub.low_score_description" class="desc-text"><i style="color:#aaa;">Rendah:</i> {{ sub.low_score_description }}</small>
                  <small v-if="sub.high_score_description" class="desc-text"><i style="color:#aaa;">Tinggi:</i> {{ sub.high_score_description }}</small>
                </div>
              </div>
              <button @click="removeSubAspectFromAspect(aspect.id, sub.id)" class="delete-btn micro">X</button>
            </li>
          </ul>
          <p v-else class="empty-sub-aspect">Belum ada sub-aspek terhubung.</p>
        </div>
      </div>

      <!-- BAGIAN 3: MANAJEMEN TES & MASTER -->
      <div class="card">
        <h2>Manajemen Tes & Master</h2>
        
        <!-- Form untuk menghubungkan Tes -->
        <form @submit.prevent="addTestAssociation" class="add-form sub-form">
          <select v-model="testAssociation.sub_aspect_id" required>
            <option value="" disabled>-- Pilih Sub-Aspek --</option>
            <template v-for="aspect in sortedAspects" :key="aspect.id">
              <optgroup :label="aspect.name">
                <option v-for="sub in aspect.sub_aspects" :key="sub.id" :value="sub.id">
                  {{ sub.name }}
                </option>
              </optgroup>
            </template>
          </select>
          <select v-model="testAssociation.test_id" required>
            <option value="" disabled>-- Pilih Master Tes --</option>
            <option v-for="test in masterTests" :key="test.id" :value="test.id">
              {{ test.name }}
            </option>
          </select>
          <button type="submit">Hubungkan Tes</button>
        </form>
        <hr>
        
        <!-- Tampilan Tes yang Terhubung -->
        <h4>Tes yang Terhubung ke Template Ini</h4>
        <div v-if="testAssociations.length > 0">
          <div v-for="aspect in sortedAspects" :key="`assoc-group-${aspect.id}`" class="aspect-group">
            <ul v-if="aspect.sub_aspects.length > 0" class="item-list sub-item-list">
              <li v-for="sub in aspect.sub_aspects" :key="sub.id" class="item sub-item">
                <span class="main-text">{{ sub.name }}</span>
                <div class="test-list-for-sub-aspect">
                  <div v-for="assoc in getTestsForSubAspect(sub.id)" :key="assoc.test.id" class="test-tag">
                    <span>{{ assoc.test.name }}</span>
                    <button @click="removeTestAssociation(assoc)" class="delete-btn micro">X</button>
                  </div>
                  <em v-if="getTestsForSubAspect(sub.id).length === 0">Belum ada tes terhubung.</em>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <p v-else class="empty-state">Belum ada tes yang terhubung ke template ini.</p>
        <hr>

        <!-- Form untuk menambah / mengedit Master Sub-Aspek -->
        <h4 v-if="editingMasterSubAspectId">Edit Master Sub-Aspek</h4>
        <h4 v-else>Tambah Master Sub-Aspek</h4>
        <form @submit.prevent="saveMasterSubAspect" class="add-form sub-aspect-form">
          <input v-model="newMasterSubAspect.name" placeholder="Nama Sub-Aspek Baru" required class="full-width-input" />
          
          <select v-model="newMasterSubAspect.minimum_category" required class="full-width-input">
            <option value="C">Minimal Kurang (C)</option>
            <option value="B">Minimal Cukup (B)</option>
            <option value="A">Minimal Baik (A)</option>
          </select>

          <div class="desc-inputs">
            <textarea v-model="newMasterSubAspect.description" placeholder="Deskripsi Umum (Format Biasa)" class="full-width-input" rows="2"></textarea>
            <textarea v-model="newMasterSubAspect.low_score_description" placeholder="Gambaran Skor Rendah (Khusus Format YPIA)" class="full-width-input" rows="2"></textarea>
            <textarea v-model="newMasterSubAspect.high_score_description" placeholder="Gambaran Skor Tinggi (Khusus Format YPIA)" class="full-width-input" rows="2"></textarea>
          </div>

          <div style="display: flex; gap: 10px;">
            <button type="submit" :disabled="isSavingMasterSubAspect" class="btn-primary" style="flex-grow: 1;">
              {{ isSavingMasterSubAspect ? 'Menyimpan...' : (editingMasterSubAspectId ? 'Update Master' : 'Tambah Master') }}
            </button>
            <button v-if="editingMasterSubAspectId" type="button" @click="cancelEditMasterSubAspect" class="btn-secondary" style="padding: 10px;">Batal</button>
          </div>
        </form>

        <hr>
        <h4>Daftar Master Sub-Aspek (Preview)</h4>
        <div class="master-preview-list">
          <div v-for="sub in masterSubAspects" :key="'master-'+sub.id" class="master-preview-card">
            <div class="master-header">
              <div>
                <strong>{{ sub.name }}</strong>
                <span class="badge">{{ sub.minimum_category }}</span>
              </div>
              <button @click="editMasterSubAspect(sub)" class="btn-secondary micro">Edit</button>
            </div>
            <div class="master-body">
              <p v-if="sub.description"><strong>Umum:</strong> {{ sub.description }}</p>
              <p v-if="sub.low_score_description"><strong>Rendah:</strong> {{ sub.low_score_description }}</p>
              <p v-if="sub.high_score_description"><strong>Tinggi:</strong> {{ sub.high_score_description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <p v-else class="empty-state">Pilih atau buat template terlebih dahulu untuk mulai mengelola struktur psikogram.</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import apiClient from '@/services/api.js';

// --- STATE ---
const templates = ref([]);
const newTemplateName = ref('');
const selectedTemplateId = ref(null);
const isCreatingTemplate = ref(false);

const aspects = ref([]);
const newAspect = ref({ name: '', order: 1 });

const masterSubAspects = ref([]);
const editingMasterSubAspectId = ref(null);
const newMasterSubAspect = ref({ 
  name: '', 
  minimum_category: 'B',
  description: '', 
  low_score_description: '', 
  high_score_description: '' 
});

const subAspectAssociation = ref({ aspect_id: '', sub_aspect_id: '' });
const testAssociation = ref({ sub_aspect_id: '', test_id: '' });
const testAssociations = ref([]);

const masterTests = ref([]);

const isLoading = ref(true);
const isSavingAspect = ref(false);
const isSavingMasterSubAspect = ref(false);

// --- COMPUTED ---
const sortedAspects = computed(() => {
  if (!aspects.value) return [];
  return [...aspects.value].sort((a, b) => a.order - b.order);
});

// --- API FUNCTIONS ---
const initialFetch = async () => {
  isLoading.value = true;
  try {
    const [tplRes, masterSubRes, testsRes] = await Promise.all([
      apiClient.get('/admin/psychogram-templates/'),
      apiClient.get('/admin/master-sub-aspects/'),
      apiClient.get('/admin/tests/master-list?limit=1000')
    ]);
    templates.value = tplRes.data;
    masterSubAspects.value = masterSubRes.data;
    masterTests.value = testsRes.data.tests;
  } catch (error) { 
    console.error("Gagal memuat data awal:", error); 
  } finally { 
    isLoading.value = false; 
  }
};

const fetchAspectsForTemplate = async () => {
  if (!selectedTemplateId.value) {
    aspects.value = [];
    return;
  }
  try {
    const response = await apiClient.get(`/admin/templates/${selectedTemplateId.value}/aspects/`);
    aspects.value = response.data;
  } catch (error) { 
    console.error(`Gagal memuat aspek`, error);
    aspects.value = [];
  }
};

const fetchTestAssociations = async () => {
    if (!selectedTemplateId.value) {
        testAssociations.value = [];
        return;
    }
    try {
        const response = await apiClient.get(`/admin/templates/${selectedTemplateId.value}/test-associations`);
        testAssociations.value = response.data;
    } catch (error) { console.error("Gagal memuat asosiasi tes:", error); }
};

// Helper function untuk template
const getTestsForSubAspect = (subAspectId) => {
    return testAssociations.value.filter(a => a.sub_aspect_id === subAspectId);
};

// --- CRUD & ACTION HANDLERS ---
const addTemplate = async () => {
  isCreatingTemplate.value = true;
  try {
    const response = await apiClient.post('/admin/psychogram-templates/', { name: newTemplateName.value });
    templates.value.push(response.data);
    selectedTemplateId.value = response.data.id;
    newTemplateName.value = '';
  } catch (error) {
    alert("Gagal membuat template: " + (error.response?.data?.detail || error.message));
  } finally {
    isCreatingTemplate.value = false;
  }
};

const addAspect = async () => {
  isSavingAspect.value = true;
  try {
    await apiClient.post(`/admin/templates/${selectedTemplateId.value}/aspects/`, newAspect.value);
    await fetchAspectsForTemplate();
    newAspect.value = { name: '', order: (aspects.value.length || 0) + 1 };
  } catch (err) { 
    alert("Gagal menambah aspek."); 
  } finally { 
    isSavingAspect.value = false; 
  }
};

const deleteAspect = async (id) => {
  if (!confirm("Yakin ingin menghapus aspek ini? Hubungannya dengan sub-aspek akan dihapus.")) return;
  try {
    await apiClient.delete(`/admin/aspects/${id}`);
    await fetchAspectsForTemplate();
  } catch (err) {
    alert("Gagal menghapus aspek.");
  }
};

const editMasterSubAspect = (sub) => {
  editingMasterSubAspectId.value = sub.id;
  newMasterSubAspect.value = {
    name: sub.name,
    minimum_category: sub.minimum_category || 'B',
    description: sub.description || '',
    low_score_description: sub.low_score_description || '',
    high_score_description: sub.high_score_description || ''
  };
};

const cancelEditMasterSubAspect = () => {
  editingMasterSubAspectId.value = null;
  newMasterSubAspect.value = { name: '', minimum_category: 'B', description: '', low_score_description: '', high_score_description: '' };
};

const saveMasterSubAspect = async () => {
  isSavingMasterSubAspect.value = true;
  try {
    if (editingMasterSubAspectId.value) {
      // Mode Edit
      const response = await apiClient.put(`/admin/master-sub-aspects/${editingMasterSubAspectId.value}`, newMasterSubAspect.value);
      const index = masterSubAspects.value.findIndex(s => s.id === editingMasterSubAspectId.value);
      if (index !== -1) masterSubAspects.value[index] = response.data;
      
      // Update juga di list aspects yang terhubung agar previewnya ter-update
      await fetchAspectsForTemplate();
      
      editingMasterSubAspectId.value = null;
    } else {
      // Mode Tambah
      const response = await apiClient.post('/admin/master-sub-aspects/', newMasterSubAspect.value);
      masterSubAspects.value.push(response.data);
    }
    newMasterSubAspect.value = { name: '', minimum_category: 'B', description: '', low_score_description: '', high_score_description: '' };
  } catch (err) {
    alert("Gagal menyimpan master sub-aspek: " + (err.response?.data?.detail || err.message));
  } finally {
    isSavingMasterSubAspect.value = false;
  }
};

const addSubAspectToAspect = async () => {
    if (!subAspectAssociation.value.aspect_id || !subAspectAssociation.value.sub_aspect_id) return;
    try {
        await apiClient.post(`/admin/aspects/${subAspectAssociation.value.aspect_id}/sub-aspects/${subAspectAssociation.value.sub_aspect_id}`);
        await fetchAspectsForTemplate();
        subAspectAssociation.value.sub_aspect_id = '';
    } catch (error) { 
      alert("Gagal menambahkan asosiasi: " + (error.response?.data?.detail || error.message)); 
    }
};

const removeSubAspectFromAspect = async (aspectId, subAspectId) => {
    if (!confirm("Yakin ingin menghapus hubungan sub-aspek ini?")) return;
    try {
        await apiClient.delete(`/admin/aspects/${aspectId}/sub-aspects/${subAspectId}`);
        await fetchAspectsForTemplate();
    } catch (error) { 
      alert("Gagal menghapus asosiasi."); 
    }
};

const addTestAssociation = async () => {
    if (!testAssociation.value.sub_aspect_id || !testAssociation.value.test_id) return;
    try {
      await apiClient.post(`/admin/templates/${selectedTemplateId.value}/test-associations`, testAssociation.value);
      await fetchTestAssociations();
      testAssociation.value.test_id = '';
    } catch (err) {
      alert("Gagal menghubungkan tes: " + (err.response?.data?.detail || err.message));
    }
};

const removeTestAssociation = async (assoc) => {
    if (!confirm("Yakin ingin memutuskan hubungan tes ini?")) return;
    try {
      await apiClient.delete(`/admin/templates/${assoc.template_id}/sub-aspects/${assoc.sub_aspect_id}/tests/${assoc.test_id}`);
      await fetchTestAssociations();
    } catch (err) {
      alert("Gagal memutuskan hubungan tes.");
    }
};

// --- LIFECYCLE & WATCHERS ---
watch(selectedTemplateId, (newId) => {
    fetchAspectsForTemplate(newId);
    fetchTestAssociations(newId);
});

onMounted(initialFetch);
</script>

<style scoped>
.psychogram-manager { max-width: 1600px; margin: auto; padding: 30px; }
.subtitle { color: #555; margin-top: -5px; margin-bottom: 25px; }
.subtitle-small { font-size: 0.9em; color: #7f8c8d; margin-bottom: 15px; }

.card { margin-bottom: 25px; background: #fff; padding: 25px; border-radius: 10px; box-shadow: 0 3px 10px rgba(0,0,0,0.06); }
.card h2 { margin-top: 0; }

.inline-form { display: flex; gap: 10px; align-items: center; }
.inline-form input { flex-grow: 1; padding: 10px; border-radius: 6px; border: 1px solid #ccc; }
.inline-form button { padding: 10px 15px; }

.template-selector { margin-top: 20px; }
.template-selector label { display: block; margin-bottom: 8px; font-weight: 500; }
.template-selector select { width: 100%; padding: 10px; border-radius: 6px; border: 1px solid #ccc; }

.manager-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(450px, 1fr)); gap: 25px; align-items: start; }
.aspect-sub-aspect-card { grid-column: 1 / 2; }
.test-management-card { grid-column: 2 / 3; }

.add-form { display: flex; flex-direction: column; gap: 15px; }
.add-form.sub-form { margin-bottom: 20px; }
.add-form input, .add-form select { width: 100%; padding: 10px; border-radius: 6px; border: 1px solid #ccc; }
.add-form button { padding: 12px; background-color: #1abc9c; color: white; border: none; border-radius: 6px; cursor: pointer; }

.item-list { list-style: none; padding: 0; margin-top: 20px; }
.item { display: flex; justify-content: space-between; align-items: center; padding: 12px; border-bottom: 1px solid #f0f0f0; }
.item:last-child { border-bottom: none; }
.delete-btn { background-color: #e74c3c; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; }
.delete-btn.small { font-size: 0.8em; padding: 4px 8px; }
.delete-btn.micro { font-size: 0.7em; padding: 2px 5px; }

.aspect-group { margin-top: 25px; }
.aspect-group h3, .aspect-header h3 { margin: 0; border-bottom: 1px solid #ddd; padding-bottom: 8px; font-size: 1.1em; }
.aspect-header { display: flex; justify-content: space-between; align-items: center; }

.empty-sub-aspect { font-style: italic; color: #888; padding: 10px; }

/* Sub Aspect Item with Descriptions */
.sub-aspect-item { align-items: flex-start; }
.sub-aspect-info { display: flex; flex-direction: column; gap: 4px; flex-grow: 1; padding-right: 15px; }
.sub-aspect-name { font-weight: 500; color: #2c3e50; }
.sub-aspect-desc-preview { display: flex; flex-direction: column; gap: 2px; }
.desc-text { font-size: 0.75em; color: #7f8c8d; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

.sub-item { flex-direction: column; align-items: stretch; }
.main-text { font-weight: 500; }
.test-list-for-sub-aspect { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 8px; }
.test-tag { background-color: #ecf0f1; color: #2c3e50; padding: 4px 8px; border-radius: 4px; font-size: 0.85em; display: inline-flex; align-items: center; gap: 6px; }

/* Styles untuk Master Sub-Aspect Preview */
.desc-inputs { display: flex; flex-direction: column; gap: 10px; }
textarea.full-width-input { width: 100%; padding: 10px; border-radius: 6px; border: 1px solid #ccc; font-family: inherit; resize: vertical; }
.master-preview-list { max-height: 500px; overflow-y: auto; display: flex; flex-direction: column; gap: 15px; padding-right: 5px; }
.master-preview-card { background: #fdfdfd; border: 1px solid #eee; padding: 15px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.master-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; border-bottom: 1px solid #f0f0f0; padding-bottom: 8px; }
.master-header strong { color: #2c3e50; font-size: 1.05em; }
.badge { background: #e8f8f5; color: #16a085; padding: 2px 8px; border-radius: 12px; font-size: 0.8em; font-weight: bold; margin-left: 8px;}
.master-body p { margin: 5px 0; font-size: 0.85em; color: #555; line-height: 1.4; }
.master-body strong { color: #34495e; margin-right: 5px; }

.btn-secondary { background-color: #95a5a6; color: white; border: none; border-radius: 6px; cursor: pointer; }
.btn-secondary.micro { font-size: 0.8em; padding: 4px 8px; border-radius: 4px; }
</style>